import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api } from "./api";
import { isRealtime } from "./realtime";
import type {
  ActiveTask,
  AppRelease,
  AuditRow,
  JobComment,
  ReleaseCommand,
  ReleaseMachines,
  CustomerDetail,
  CustomerListItem,
  CustomerSummary,
  Device,
  Job,
  MachineDetail,
  MachineRow,
  Member,
  Overview,
  Plan,
  SeoKeyword,
  CustomerStatusView,
  StatusTimeline,
  Quota,
  ResultReviewRow,
  ResultReviewSummary,
  RunDetail,
  RunRow,
  Session,
  Setting,
  TaskDetail,
  TaskEvent,
  TaskRow,
  Template,
} from "./types";

/**
 * 주기 갱신: 실시간 채널(/console)이 붙어 있으면 신호로 바로 갱신되므로 길게, 끊겼으면 짧게.
 */
export const LIVE = () => (isRealtime() ? 60_000 : 5_000);
export const SLOW = () => (isRealtime() ? 120_000 : 30_000);

const c = (customerId: string) => `/c/${customerId}`;
const qs = (o: Record<string, string | number | undefined | null>) => {
  const p = Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return p.length ? `?${p.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&")}` : "";
};

export const keys = {
  overview: ["admin", "overview"] as const,
  customers: (q: string) => ["admin", "customers", q] as const,
  customerDetail: (id: string) => ["admin", "customer", id] as const,
  adminMachines: ["admin", "machines"] as const,
  adminRuns: (f: object) => ["admin", "runs", f] as const,
  adminAudit: (o: number) => ["admin", "audit", o] as const,
  staff: ["admin", "members", "staff"] as const,
  plans: ["admin", "plans"] as const,
  settings: ["admin", "settings"] as const,
  templates: ["admin", "templates"] as const,
  summary: (id: string) => ["c", id, "summary"] as const,
  machines: (id: string) => ["c", id, "machines"] as const,
  machine: (id: string, m: string) => ["c", id, "machine", m] as const,
  devices: (id: string) => ["c", id, "devices"] as const,
  tasks: (id: string, f: object) => ["c", id, "tasks", f] as const,
  task: (id: string, t: string) => ["c", id, "task", t] as const,
  taskEvents: (id: string, t: string) => ["c", id, "task", t, "events"] as const,
  runs: (id: string, f: object) => ["c", id, "runs", f] as const,
  run: (scope: string, r: string) => ["run", scope, r] as const,
  jobs: (id: string) => ["c", id, "jobs"] as const,
  usage: (id: string) => ["c", id, "usage"] as const,
  members: (id: string) => ["c", id, "members"] as const,
  audit: (id: string, o: number) => ["c", id, "audit", o] as const,
  sessions: ["me", "sessions"] as const,
  seoKeywords: (scope: string, f: object) => ["seo", scope, f] as const,
  results: (f: object) => ["admin", "results", "list", f] as const,
  resultSummary: (customerId: string) => ["admin", "results", "summary", customerId] as const,
  result: (taskId: string) => ["admin", "results", "one", taskId] as const,
  comments: (id: string, jobId: string) => ["c", id, "job", jobId, "comments"] as const,
  releases: ["admin", "releases"] as const,
  releaseMachines: (customerId: string) => ["admin", "releases", "machines", customerId] as const,
  releaseCommands: (f: object) => ["admin", "releases", "commands", f] as const,
};

/* ---------- 전체 관리 ---------- */
/** 진행 중 작업 (상황판 진행 중 카드·실행 이력 고정 줄, 디자인 요청서 7-2). 키가 ["admin","runs"] 로 시작해 실행 신호로 함께 갱신된다. */
export const useActiveTasks = (enabled = true) =>
  useQuery({ queryKey: ["admin", "runs", "active"] as const, queryFn: () => api.page<ActiveTask>("/admin/runs/active"), enabled, refetchInterval: LIVE });
export const useOverview = () => useQuery({ queryKey: keys.overview, queryFn: () => api.get<Overview>("/admin/overview"), refetchInterval: LIVE });
export const useCustomers = (q = "", enabled = true) =>
  useQuery({ queryKey: keys.customers(q), queryFn: () => api.page<CustomerListItem>(`/admin/customers${qs({ q, limit: 200 })}`), refetchInterval: SLOW, enabled });
export const useCustomerDetail = (id: string | null) =>
  useQuery({ queryKey: keys.customerDetail(id ?? ""), queryFn: () => api.get<CustomerDetail>(`/admin/customers/${id}`), enabled: !!id });
export const useAdminMachines = () => useQuery({ queryKey: keys.adminMachines, queryFn: () => api.page<MachineRow>("/admin/machines"), refetchInterval: LIVE });
export type RunFilter = { status?: string; customerId?: string; errorKind?: string; origin?: string; kind?: string; from?: string; to?: string; offset?: number; limit?: number };
export const useAdminRuns = (f: RunFilter, enabled = true) =>
  useQuery({ queryKey: keys.adminRuns(f), queryFn: () => api.page<RunRow>(`/admin/runs${qs({ ...f, limit: f.limit ?? 50 })}`), refetchInterval: LIVE, enabled });
export const useAdminAudit = (offset: number) =>
  useQuery({ queryKey: keys.adminAudit(offset), queryFn: () => api.page<AuditRow>(`/admin/audit${qs({ offset, limit: 50 })}`) });
export const useStaff = () => useQuery({ queryKey: keys.staff, queryFn: () => api.page<Member>("/admin/members?scope=staff&limit=200") });
export const usePlans = () => useQuery({ queryKey: keys.plans, queryFn: () => api.page<Plan>("/admin/plans") });
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: () => api.get<Setting[]>("/admin/settings") });
export const useTemplates = () => useQuery({ queryKey: keys.templates, queryFn: () => api.page<Template>("/admin/templates") });

/* ---------- 고객 화면 ---------- */
export const useSummary = (id: string) =>
  useQuery({ queryKey: keys.summary(id), queryFn: () => api.get<CustomerSummary>(c(id)), refetchInterval: SLOW, enabled: !!id });
export const useMachines = (id: string) => useQuery({ queryKey: keys.machines(id), queryFn: () => api.page<MachineRow>(`${c(id)}/machines`), refetchInterval: LIVE, enabled: !!id });
export const useMachine = (id: string, m: string) =>
  useQuery({ queryKey: keys.machine(id, m), queryFn: () => api.get<MachineDetail>(`${c(id)}/machines/${m}`), refetchInterval: LIVE, enabled: !!m });
export const useDevices = (id: string) => useQuery({ queryKey: keys.devices(id), queryFn: () => api.page<Device>(`${c(id)}/devices`), refetchInterval: LIVE, enabled: !!id });
export type TaskFilter = { status?: string; machineId?: string; offset?: number; limit?: number };
export const useTasks = (id: string, f: TaskFilter = {}) =>
  useQuery({ queryKey: keys.tasks(id, f), queryFn: () => api.page<TaskRow>(`${c(id)}/tasks${qs({ ...f, limit: f.limit ?? 50 })}`), refetchInterval: LIVE });
export const useTask = (id: string, t: string | null) =>
  useQuery({ queryKey: keys.task(id, t ?? ""), queryFn: () => api.get<TaskDetail>(`${c(id)}/tasks/${t}`), enabled: !!t, refetchInterval: LIVE });
export const useTaskEvents = (id: string, t: string | null) =>
  useQuery({ queryKey: keys.taskEvents(id, t ?? ""), queryFn: () => api.page<TaskEvent>(`${c(id)}/tasks/${t}/events?tail=500`), enabled: !!t, refetchInterval: LIVE });
export const useRuns = (id: string, f: RunFilter = {}) =>
  useQuery({ queryKey: keys.runs(id, f), queryFn: () => api.page<RunRow>(`${c(id)}/runs${qs({ ...f, limit: f.limit ?? 50 })}`), refetchInterval: LIVE });
/** Run 상세: 직원은 전체 관리 경로, 고객 화면은 고객 경로. */
export const useRun = (customerId: string | null, runId: string | null) =>
  useQuery({
    queryKey: keys.run(customerId ?? "admin", runId ?? ""),
    queryFn: () => api.get<RunDetail>(customerId ? `${c(customerId)}/runs/${runId}` : `/admin/runs/${runId}`),
    enabled: !!runId,
    refetchInterval: LIVE,
  });
export const useJobs = (id: string) => useQuery({ queryKey: keys.jobs(id), queryFn: () => api.page<Job>(`${c(id)}/jobs`), refetchInterval: SLOW });
export const useUsage = (id: string) => useQuery({ queryKey: keys.usage(id), queryFn: () => api.get<Quota>(`${c(id)}/usage`), refetchInterval: SLOW });
export const useMembers = (id: string) => useQuery({ queryKey: keys.members(id), queryFn: () => api.page<Member>(`${c(id)}/members?limit=200`) });
export const useAudit = (id: string, offset: number) =>
  useQuery({ queryKey: keys.audit(id, offset), queryFn: () => api.page<AuditRow>(`${c(id)}/audit${qs({ offset, limit: 50 })}`) });
/** SEO 추적 키워드: 직원은 전체 관리 경로(고객사 선택), 고객은 자기 고객사 경로. */
export const useAdminSeoKeywords = (f: { customerId?: string; status?: string; q?: string }) =>
  useQuery({ queryKey: keys.seoKeywords("admin", f), queryFn: () => api.page<SeoKeyword>(`/admin/seo/keywords${qs({ ...f, limit: 200 })}`), refetchInterval: SLOW });
export const useCustomerSeoKeywords = (customerId: string) =>
  useQuery({ queryKey: keys.seoKeywords(customerId, {}), queryFn: () => api.page<SeoKeyword>(`${c(customerId)}/seo/keywords?limit=200`), refetchInterval: SLOW });
/** 시스템 상태 요약 (고객 현황·데스크톱 현황). 머신 상태 신호(실시간)로 갱신된다. */
export const useCustomerStatus = (customerId: string) =>
  useQuery({ queryKey: ["c", customerId, "status"], queryFn: () => api.get<CustomerStatusView>(`${c(customerId)}/status`), refetchInterval: LIVE, enabled: !!customerId });
/** 머신별 24시간 연결 구간 (현황 타임라인). */
export const useStatusTimeline = (customerId: string, hours = 24) =>
  useQuery({ queryKey: ["c", customerId, "status", "timeline", hours], queryFn: () => api.get<StatusTimeline>(`${c(customerId)}/status/timeline?hours=${hours}`), refetchInterval: SLOW, enabled: !!customerId });
/* ---------- 결과 검토함 (직원 전용, 시안 6a084bb) ---------- */
export type ResultFilter = { status?: string; customerId?: string; offset?: number; limit?: number };
export const useResultReviews = (f: ResultFilter, enabled = true) =>
  useQuery({ queryKey: keys.results(f), queryFn: () => api.page<ResultReviewRow>(`/admin/results${qs({ ...f, status: f.status ?? "all", limit: f.limit ?? 100 })}`), refetchInterval: LIVE, enabled });
export const useResultReviewSummary = (customerId = "", enabled = true) =>
  useQuery({ queryKey: keys.resultSummary(customerId), queryFn: () => api.get<ResultReviewSummary>(`/admin/results/summary${qs({ customerId })}`), refetchInterval: LIVE, enabled });
export const useResultReview = (taskId: string | null) =>
  useQuery({ queryKey: keys.result(taskId ?? ""), queryFn: () => api.get<ResultReviewRow>(`/admin/results/${taskId}`), enabled: !!taskId, refetchInterval: LIVE });
export const useSessions = () => useQuery({ queryKey: keys.sessions, queryFn: () => api.page<Session>("/auth/sessions") });
/* ---------- 고객 발행 요청 스레드 (2026-10-16) ---------- */
export const useJobComments = (customerId: string | null, jobId: string | null) =>
  useQuery({ queryKey: keys.comments(customerId ?? "", jobId ?? ""), queryFn: () => api.page<JobComment>(`${c(customerId!)}/jobs/${jobId}/comments`), enabled: !!customerId && !!jobId, refetchInterval: LIVE });
/** 발행 요청 원고 버전 목록 (버전 비교용). */
export type JobRevision = { revision: number; createdAt: string; manuscript: string | null; files: { id: string; name: string | null }[] };
export const useJobRevisions = (customerId: string | null, jobId: string | null) =>
  useQuery({ queryKey: ["c", customerId ?? "", "job", jobId ?? "", "revisions"], queryFn: () => api.page<JobRevision>(`${c(customerId!)}/jobs/${jobId}/revisions`), enabled: !!customerId && !!jobId });
/* ---------- 릴리즈·원격 업데이트 (직원) ---------- */
export const useReleases = (enabled = true) => useQuery({ queryKey: keys.releases, queryFn: () => api.page<AppRelease>("/admin/releases"), refetchInterval: SLOW, enabled });
export const useReleaseMachines = (customerId = "", enabled = true) =>
  useQuery({ queryKey: keys.releaseMachines(customerId), queryFn: () => api.get<ReleaseMachines>(`/admin/releases/machines${qs({ customerId })}`), refetchInterval: LIVE, enabled });
export const useReleaseCommands = (f: { machineId?: string; customerId?: string; open?: boolean }, enabled = true) =>
  useQuery({ queryKey: keys.releaseCommands(f), queryFn: () => api.page<ReleaseCommand>(`/admin/releases/commands${qs({ machineId: f.machineId, customerId: f.customerId, open: f.open ? 1 : undefined })}`), refetchInterval: LIVE, enabled });

/** 쓰기 요청 + 관련 목록 다시 읽기. */
export function useAction<TVars, TResult = unknown>(fn: (v: TVars) => Promise<TResult>, invalidate: QueryKey[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
    },
  });
}
